import { useCallback, useEffect } from "react";
import { useRouter } from "next/router";

import clsx from "clsx";

import ApiError from "api/Error";

import { SingleColumnSelect } from "modules/data-files/elements/Column-Select";

import * as flowConfig from "flows/eta-estimator/config";

import SelectInputData from "./Select-Input-Data";

import { useGetCreate } from "./api";
import { useInputFile } from "./params";
import {
  useOriginLatColumn,
  useOriginLongColumn,
  useDestinationLatColumn,
  useDestinationLongColumn,
} from "./params";
import { useStartTimeColumn, useEndTimeColumn } from "./params";
import { useGroupingColumns } from "./params";

const CreateProject = () => {
  const router = useRouter();
  const data = router.query.data as string;
  const { push } = router;

  const [currentFile, setCurrentFile] = useInputFile();
  useEffect(() => {
    if (data && data !== currentFile) {
      setCurrentFile(data);
    }
  }, [data, currentFile]);

  const [createProject, submitStatus, error] = useGetCreate();

  const create = useCallback(async () => {
    const resp = await createProject();
    if (resp?.success) {
      push(`/${flowConfig.FLOW_NAME}/${resp.project.id}`);
    }
  }, [createProject, push]);

  const [originLat, setOriginLat] = useOriginLatColumn();
  const [originLong, setOriginLong] = useOriginLongColumn();
  const [destLat, setDestLat] = useDestinationLatColumn();
  const [destLong, setDestLong] = useDestinationLongColumn();
  const [startTime, setStartTime] = useStartTimeColumn();
  const [endTime, setEndTime] = useEndTimeColumn();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <SelectInputData />
      {currentFile && (
        <>
          {/* Origin Lat/Long */}
          <p className="font-bold mt-2 pt-2">
            2. Origin Latitude and Longitude
          </p>
          <div className="grid md:grid-cols-2 gap-6 px-2 mx-1">
            <div className="flex mt-2 pt-2 items-center">
              <div className="w-1/4">
                <label htmlFor={"origin-latitude"} className="font-bold">
                  2a. Latitude Column:
                </label>
              </div>
              <div className="w-full">
                <SingleColumnSelect
                  name="origin-latitude"
                  fileId={currentFile}
                  currentSelection={originLat}
                  onChange={setOriginLat}
                  allowedTypes={["numeric"]}
                  placeholder="Select Origin Latitude Column"
                />
              </div>
            </div>

            <div className="flex mt-2 pt-2 items-center">
              <div className="w-1/4">
                <label htmlFor={"origin-longitude"} className="font-bold">
                  2b. Longitude Column:
                </label>
              </div>
              <div className="w-full">
                <SingleColumnSelect
                  name="origin-longitude"
                  fileId={currentFile}
                  currentSelection={originLong}
                  onChange={setOriginLong}
                  allowedTypes={["numeric"]}
                  placeholder="Select Origin Longitude Column"
                />
              </div>
            </div>
          </div>
          {/* Dest Lat/Long */}
          <p className="font-bold mt-2 pt-2">
            3. Destination Latitude and Longitude
          </p>
          <div className="grid md:grid-cols-2 gap-6 px-2 mx-1">
            <div className="flex mt-2 pt-2 items-center">
              <div className="w-1/4">
                <label htmlFor={"dest-latitude"} className="font-bold">
                  3a. Latitude Column:
                </label>
              </div>
              <div className="w-full">
                <SingleColumnSelect
                  name="dest-latitude"
                  fileId={currentFile}
                  currentSelection={destLat}
                  onChange={setDestLat}
                  allowedTypes={["numeric"]}
                  placeholder="Select Destination Latitude Column"
                />
              </div>
            </div>
            <div className="flex mt-2 pt-2 items-center">
              <div className="w-1/4">
                <label htmlFor={"dest-longitude"} className="font-bold">
                  3b. Longitude Column:
                </label>
              </div>
              <div className="w-full">
                <SingleColumnSelect
                  name="dest-longitude"
                  fileId={currentFile}
                  currentSelection={destLong}
                  onChange={setDestLong}
                  allowedTypes={["numeric"]}
                  placeholder="Select Destination Longitude Column"
                />
              </div>
            </div>
          </div>
          {/* Start and End Time */}
          <p className="font-bold mt-2 pt-2">4. Time Columns</p>
          <div className="grid md:grid-cols-2 gap-6 px-2 mx-1">
            <div className="flex mt-2 pt-2 items-center">
              <div className="w-1/4">
                <label htmlFor={"start-time"} className="font-bold">
                  4a. Start Time:
                </label>
              </div>
              <div className="w-full">
                <SingleColumnSelect
                  name="start-time"
                  fileId={currentFile}
                  currentSelection={startTime}
                  onChange={setStartTime}
                  allowedTypes={["date"]}
                  placeholder="Select Start Time Column"
                />
              </div>
            </div>

            <div className="flex mt-2 pt-2 items-center">
              <div className="w-1/4">
                <label htmlFor={"end-time"} className="font-bold">
                  4b. End Time:
                </label>
              </div>
              <div className="w-full">
                <SingleColumnSelect
                  name="end-time"
                  fileId={currentFile}
                  currentSelection={endTime}
                  onChange={setEndTime}
                  allowedTypes={["date"]}
                  placeholder="Select End Time Column"
                />
              </div>
            </div>
          </div>
          {/* <div className="flex mt-2 pt-2 items-center">
            <div className="w-1/4">
              <label htmlFor={"grouping-variables"} className="font-bold">
                4. Grouping Variables (optional):
              </label>
            </div>
            <div className="w-full">
              <MultiColumnSelect
                name="grouping-variables"
                fileId={currentFile}
                currentSelection={groupingColumns}
                onChange={setGroupingColumns}
                allowedTypes={["char"]}
                placeholder="Select Grouping Columns"
              />
            </div>
          </div> */}
        </>
      )}
      {submitStatus === "completed" && error && <ApiError error={error} />}
      {currentFile &&
        !!originLat &&
        !!originLong &&
        !!destLat &&
        !!destLong &&
        !!startTime &&
        !!endTime && (
          <div className="text-center my-4 py-4">
            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-3">
                <button
                  className={clsx(
                    "btn px-0",
                    "text-white",
                    "bg-indigo-600 hover:bg-indigo-700",
                    "w-full",
                    "relative",
                    "flex items-center"
                  )}
                  disabled={submitStatus === "in-progress"}
                  onClick={create}
                >
                  {submitStatus === "in-progress" && (
                    <svg
                      className="animate-spin h-5 w-5 mr-3  text-white"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {submitStatus === "in-progress"
                    ? "Creating..."
                    : "Create New Project"}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default CreateProject;
